/// IRPCS Part B — Steering and Sailing Rules — drilled as animated
/// scenarios. The player watches two vessels approaching, picks the
/// correct response from a multiple-choice list, and sees the rule
/// citation in the reveal.
///
/// Coordinates use a top-down 100×100 viewbox with NORTH up
/// (heading 0°). A vessel with heading 90° moves east (positive x);
/// 180° moves south (positive y). Position units are arbitrary —
/// the renderer scales the viewbox to whatever pixel size the
/// caller asks for.

export type VesselType =
  | "power"
  | "sailing-port-tack"
  | "sailing-stbd-tack"
  | "fishing"
  | "nuc"
  | "ram"
  | "constrained";

export function vesselTypeLabel(t: VesselType): string {
  switch (t) {
    case "power":
      return "Power-driven";
    case "sailing-port-tack":
      return "Sailing, port tack";
    case "sailing-stbd-tack":
      return "Sailing, starboard tack";
    case "fishing":
      return "Fishing";
    case "nuc":
      return "Not under command";
    case "ram":
      return "Restricted in ability to manoeuvre";
    case "constrained":
      return "Constrained by draft";
  }
}

export interface ScenarioVessel {
  /// One-letter label drawn on the vessel sprite ("A", "B").
  label: string;
  type: VesselType;
  /// Starting position on the 100×100 viewbox.
  x0: number;
  y0: number;
  /// Heading in degrees, 0° = north (up), clockwise.
  heading: number;
  /// Speed in viewbox-units per animation second. The animation
  /// runs for `Scenario.durationMs`, so a speed of 12 over 4s
  /// will travel 48 viewbox units.
  speed: number;
}

export interface ScenarioAction {
  id: string;
  /// Text on the multiple-choice button.
  label: string;
  /// Whether this action is the correct response.
  correct: boolean;
  /// Optional follow-up scenario triggered by this action. When set,
  /// the reveal panel offers "Continue scenario →" instead of "Next
  /// scenario", and the player jumps directly to the linked card.
  /// Used for multi-stage drills like "you altered to stbd but B
  /// didn't react; what now?".
  followUpId?: string;
}

export interface Scenario {
  id: string;
  title: string;
  /// One-paragraph setup shown above the animation.
  setup: string;
  /// IRPCS rule + section citation surfaced on the reveal.
  rule: string;
  /// Plain-English explainer for the reveal panel.
  explanation: string;
  vessels: readonly ScenarioVessel[];
  actions: readonly ScenarioAction[];
  /// How long the approach animation runs before the player must
  /// answer. Default 4000 ms.
  durationMs: number;
}

/// Starter set of scenarios — covers the most-tested Part B rules.
/// Each one is built so the geometry alone makes the answer
/// obvious *if* you know the rule; geometry + rule = right answer.
export const SCENARIOS: Readonly<Record<string, Scenario>> = Object.freeze({
  crossing_power_a_gives_way: {
    id: "crossing_power_a_gives_way",
    title: "Crossing — two power-driven vessels",
    setup:
      "You are on vessel A (heading east). Vessel B is approaching from your starboard bow on a steady bearing. Both are power-driven, under way, in open water.",
    rule: "Rule 15 — Crossing situation.",
    explanation:
      "When two power-driven vessels are crossing so as to involve risk of collision, the vessel which has the other on her own starboard side shall keep out of the way. Vessel A has B on her starboard — A gives way (Rule 16: take early, substantial action; typically alter to starboard to pass astern of B).",
    vessels: [
      {
        label: "A",
        type: "power",
        x0: 10,
        y0: 50,
        heading: 90,
        speed: 10,
      },
      {
        label: "B",
        type: "power",
        x0: 70,
        y0: 90,
        heading: 340,
        speed: 10,
      },
    ],
    actions: [
      {
        id: "alter_stbd_pass_astern",
        label: "Alter A to starboard, pass astern of B",
        correct: true,
      },
      {
        id: "hold_course",
        label: "A holds course, B gives way",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both vessels alter to starboard",
        correct: false,
      },
      {
        id: "a_to_port",
        label: "Alter A to port, pass ahead of B",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  head_on_power: {
    id: "head_on_power",
    title: "Head-on — two power-driven vessels",
    setup:
      "Two power-driven vessels meeting on reciprocal courses with the risk of collision. Both vessels are clearly visible to each other.",
    rule: "Rule 14 — Head-on situation.",
    explanation:
      "When two power-driven vessels are meeting on reciprocal or nearly reciprocal courses so as to involve risk of collision, each shall alter her course to starboard so that each shall pass on the port side of the other. Both alter — neither holds course.",
    vessels: [
      {
        label: "A",
        type: "power",
        x0: 50,
        y0: 85,
        heading: 0,
        speed: 10,
      },
      {
        label: "B",
        type: "power",
        x0: 50,
        y0: 15,
        heading: 180,
        speed: 10,
      },
    ],
    actions: [
      {
        id: "both_alter_stbd",
        label: "Both vessels alter to starboard",
        correct: true,
      },
      {
        id: "stbd_is_stand_on",
        label: "The vessel with the other on her port side stands on",
        correct: false,
      },
      {
        id: "both_to_port",
        label: "Both vessels alter to port",
        correct: false,
      },
      {
        id: "smaller_gives_way",
        label: "The smaller vessel gives way",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  overtaking: {
    id: "overtaking",
    title: "Overtaking",
    setup:
      "You are on vessel A, approaching vessel B from astern. Your bearing to B is steady; you are clearly more than 22.5° abaft her beam.",
    rule: "Rule 13 — Overtaking.",
    explanation:
      "Any vessel overtaking any other shall keep out of the way of the vessel being overtaken. The overtaking vessel is defined as one approaching from more than 22.5° abaft the beam — even a sailing vessel overtaking a power vessel gives way. The relative bearing rule overrides every other steering rule.",
    vessels: [
      {
        label: "A",
        type: "power",
        x0: 50,
        y0: 90,
        heading: 0,
        speed: 14,
      },
      {
        label: "B",
        type: "power",
        x0: 50,
        y0: 55,
        heading: 0,
        speed: 5,
      },
    ],
    actions: [
      {
        id: "a_keeps_clear",
        label: "Vessel A (overtaking) keeps clear",
        correct: true,
      },
      {
        id: "b_alters",
        label: "Vessel B alters course to starboard",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
      {
        id: "a_stands_on",
        label: "Vessel A holds course and speed",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  power_meets_sail: {
    id: "power_meets_sail",
    title: "Power meets sail",
    setup:
      "Vessel A is power-driven, under way. Vessel B is sailing under canvas only (no engine). Both are crossing in open water in good visibility.",
    rule: "Rule 18 — Responsibilities between vessels.",
    explanation:
      "A power-driven vessel under way shall keep out of the way of a sailing vessel (Rule 18(a)(iv)). Rule 18 doesn't apply in narrow channels (Rule 9) or to vessels overtaking — but in open water, this is unambiguous.",
    vessels: [
      {
        label: "A",
        type: "power",
        x0: 12,
        y0: 50,
        heading: 90,
        speed: 10,
      },
      {
        label: "B",
        type: "sailing-stbd-tack",
        x0: 60,
        y0: 85,
        heading: 340,
        speed: 7,
      },
    ],
    actions: [
      {
        id: "a_gives_way",
        label: "Power vessel A gives way",
        correct: true,
      },
      {
        id: "b_gives_way",
        label: "Sailing vessel B gives way (smaller)",
        correct: false,
      },
      {
        id: "stbd_stand_on",
        label: "The vessel with the other on her port side stands on",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  sail_port_meets_stbd: {
    id: "sail_port_meets_stbd",
    title: "Sailing — different tacks",
    setup:
      "Two sailing vessels approaching with the wind on different sides. Vessel A has the wind on her port side; vessel B has the wind on her starboard side.",
    rule: "Rule 12 — Sailing vessels.",
    explanation:
      "When two sailing vessels are approaching one another so as to involve risk of collision and they have the wind on different sides, the vessel which has the wind on the port side shall keep out of the way of the other. Vessel A (port tack) gives way.",
    vessels: [
      {
        label: "A",
        type: "sailing-port-tack",
        x0: 12,
        y0: 50,
        heading: 90,
        speed: 8,
      },
      {
        label: "B",
        type: "sailing-stbd-tack",
        x0: 88,
        y0: 50,
        heading: 270,
        speed: 8,
      },
    ],
    actions: [
      {
        id: "a_gives_way",
        label: "Vessel A (port tack) gives way",
        correct: true,
      },
      {
        id: "b_gives_way",
        label: "Vessel B (starboard tack) gives way",
        correct: false,
      },
      {
        id: "windward_gives_way",
        label: "The windward vessel gives way",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  sail_same_tack_windward_gives_way: {
    id: "sail_same_tack_windward_gives_way",
    title: "Sailing — same tack",
    setup:
      "Two sailing vessels both on the starboard tack (wind on starboard side). Vessel A is to windward (upwind) of vessel B.",
    rule: "Rule 12 — Sailing vessels (same tack).",
    explanation:
      "When both sailing vessels have the wind on the same side, the vessel which is to windward shall keep out of the way of the vessel which is to leeward. Windward (A) gives way.",
    vessels: [
      {
        label: "A",
        type: "sailing-stbd-tack",
        x0: 30,
        y0: 20,
        heading: 200,
        speed: 8,
      },
      {
        label: "B",
        type: "sailing-stbd-tack",
        x0: 70,
        y0: 50,
        heading: 200,
        speed: 8,
      },
    ],
    actions: [
      {
        id: "a_gives_way",
        label: "Vessel A (windward) gives way",
        correct: true,
      },
      {
        id: "b_gives_way",
        label: "Vessel B (leeward) gives way",
        correct: false,
      },
      {
        id: "stbd_tack_priority",
        label: "Whoever is on starboard tack stands on",
        correct: false,
      },
      {
        id: "overtaking_rule",
        label: "Overtaking rules apply — astern vessel keeps clear",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  power_meets_fishing: {
    id: "power_meets_fishing",
    title: "Power meets fishing",
    setup:
      "Vessel A is power-driven, under way. Vessel B is engaged in fishing (trawling) — green-over-white all-round + diamond by day.",
    rule: "Rule 18(a)(iii) — Responsibilities between vessels.",
    explanation:
      "A power-driven vessel under way shall keep out of the way of a vessel engaged in fishing. Rule 18 establishes the hierarchy: NUC > RAM > fishing > sailing > power. Sailing vessels also give way to fishing vessels.",
    vessels: [
      {
        label: "A",
        type: "power",
        x0: 15,
        y0: 60,
        heading: 60,
        speed: 10,
      },
      {
        label: "B",
        type: "fishing",
        x0: 70,
        y0: 30,
        heading: 200,
        speed: 4,
      },
    ],
    actions: [
      {
        id: "a_gives_way",
        label: "Power vessel A gives way",
        correct: true,
      },
      {
        id: "b_gives_way",
        label: "Fishing vessel B gives way",
        correct: false,
      },
      {
        id: "stbd_rule",
        label: "Apply crossing rule (stbd has stand-on)",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  power_meets_nuc: {
    id: "power_meets_nuc",
    title: "Power meets vessel not under command",
    setup:
      "Vessel A is power-driven, under way. Vessel B is not under command — engine failure, drifting — showing two all-round red lights at night.",
    rule: "Rule 18(a)(i) — Responsibilities between vessels.",
    explanation:
      "A power-driven vessel under way shall keep out of the way of a vessel not under command. NUC sits at the top of the Rule 18 priority list — every other category of vessel must keep clear of her, since by definition she can't manoeuvre to keep clear of you.",
    vessels: [
      {
        label: "A",
        type: "power",
        x0: 12,
        y0: 40,
        heading: 90,
        speed: 10,
      },
      {
        label: "B",
        type: "nuc",
        x0: 65,
        y0: 60,
        heading: 0,
        speed: 1,
      },
    ],
    actions: [
      {
        id: "a_gives_way",
        label: "Power vessel A gives way",
        correct: true,
      },
      {
        id: "b_alters",
        label: "NUC vessel B alters course",
        correct: false,
      },
      {
        id: "stbd_rule",
        label: "Apply crossing rule (stbd has stand-on)",
        correct: false,
      },
      {
        id: "smaller_gives_way",
        label: "Smaller vessel gives way",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  crossing_power_a_stand_on: {
    id: "crossing_power_a_stand_on",
    title: "Crossing — you are the stand-on vessel",
    setup:
      "You are on vessel A (heading east). Vessel B is approaching from your port bow on a steady bearing. Both are power-driven, under way, in open water.",
    rule: "Rules 15, 16, 17 — Crossing situation; action by stand-on.",
    explanation:
      "A has B on her port side, so B is the give-way vessel (Rule 15). A is stand-on under Rule 17(a)(i) and shall keep her course and speed. The stand-on vessel may take action under Rule 17(a)(ii) only when it becomes apparent the give-way vessel isn't responding — and Rule 17(c) forbids altering to port for a crossing vessel on her port side.",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 60, y0: 10, heading: 200, speed: 10 },
    ],
    actions: [
      {
        id: "hold_course",
        label: "Hold course and speed — A is stand-on",
        correct: true,
        // Multi-stage: the player held course correctly, but in the
        // follow-up scenario B is still on a collision bearing.
        followUpId: "stand_on_takes_action",
      },
      { id: "alter_stbd", label: "Alter A to starboard", correct: false },
      { id: "alter_port", label: "Alter A to port", correct: false },
      { id: "both_stbd", label: "Both alter to starboard", correct: false },
    ],
    durationMs: 4000,
  },
  stand_on_takes_action: {
    id: "stand_on_takes_action",
    title: "Stand-on — give-way vessel isn't responding",
    setup:
      "You are vessel A, stand-on in a crossing situation. Vessel B (give-way) is still on a steady collision bearing and has shown no sign of altering. CPA is shrinking dangerously.",
    rule: "Rule 17(a)(ii) + 17(c).",
    explanation:
      "Once it becomes apparent that the give-way vessel is not taking appropriate action, the stand-on vessel may manoeuvre to avoid collision (Rule 17(a)(ii)). Rule 17(c) forbids altering to port when the other vessel is on your port side — a turn to starboard (astern of B) or stopping is the correct action. First sound 5 short blasts (Rule 34(d)).",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 50, y0: 12, heading: 200, speed: 10 },
    ],
    actions: [
      {
        id: "alter_stbd_astern",
        label: "Alter A to starboard, pass astern of B",
        correct: true,
      },
      {
        id: "still_hold",
        label: "Hold course — you are stand-on, never deviate",
        correct: false,
      },
      {
        id: "alter_port",
        label: "Alter A to port, pass ahead of B",
        correct: false,
      },
      {
        id: "increase_speed",
        label: "Sound 5 short blasts and increase speed to clear ahead",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  restricted_visibility: {
    id: "restricted_visibility",
    title: "Restricted visibility — fog signal forward of beam",
    setup:
      "You are in fog. You hear another vessel's fog signal apparently forward of your beam. You are power-driven, making way.",
    rule: "Rule 19 — Conduct of vessels in restricted visibility.",
    explanation:
      "Rule 19(e): every vessel which hears apparently forward of her beam the fog signal of another vessel shall reduce her speed to the minimum at which she can be kept on her course, and if necessary take all her way off. Rule 19(d) avoids altering to port for a vessel forward of the beam. There is no stand-on vessel in restricted visibility.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 85, heading: 0, speed: 8 },
      { label: "B", type: "power", x0: 55, y0: 20, heading: 180, speed: 8 },
    ],
    actions: [
      {
        id: "reduce_speed",
        label: "Reduce to bare steerage way; be prepared to stop",
        correct: true,
      },
      {
        id: "alter_stbd_normal",
        label: "Alter to starboard as in a clear-visibility head-on",
        correct: false,
      },
      {
        id: "hold_speed",
        label: "Hold course and speed — you are stand-on",
        correct: false,
      },
      { id: "alter_port", label: "Alter to port to clear", correct: false },
    ],
    durationMs: 4000,
  },
  restricted_visibility_radar_only: {
    id: "restricted_visibility_radar_only",
    title: "Fog — radar contact only",
    setup:
      "You are in dense fog and have detected another vessel on radar alone. Plotting shows a close-quarters situation is developing with the contact on your port bow.",
    rule: "Rule 19(d) — Conduct in restricted visibility.",
    explanation:
      "Rule 19(d): a vessel which detects by radar alone the presence of another shall determine if a close-quarters situation is developing and take avoiding action in ample time. Such action shall avoid (i) an alteration of course to port for a vessel forward of the beam (other than for a vessel being overtaken) and (ii) an alteration of course toward a vessel abeam or abaft the beam.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 85, heading: 0, speed: 6 },
      { label: "B", type: "power", x0: 35, y0: 30, heading: 150, speed: 8 },
    ],
    actions: [
      {
        id: "alter_stbd_early",
        label: "Alter to starboard early and substantially",
        correct: true,
      },
      { id: "alter_port", label: "Alter to port to clear", correct: false },
      {
        id: "hold_speed",
        label: "Hold course — radar contacts are not crossing rules",
        correct: false,
      },
      {
        id: "alter_toward_b",
        label: "Alter toward B to pass close ahead",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  narrow_channel_sail: {
    id: "narrow_channel_sail",
    title: "Narrow channel — sailing v. large power",
    setup:
      "You are vessel A, a sailing yacht in a narrow channel. Vessel B is a deep-draft container ship transiting the channel — she can navigate safely only within it.",
    rule: "Rule 9 — Narrow channels.",
    explanation:
      "Rule 9(b): a vessel of less than 20 m in length or a sailing vessel shall not impede the passage of a vessel which can safely navigate only within a narrow channel or fairway. Rule 9 overrides Rule 18 here — the sailing vessel keeps clear even though power would normally give way to sail.",
    vessels: [
      {
        label: "A",
        type: "sailing-stbd-tack",
        x0: 50,
        y0: 80,
        heading: 0,
        speed: 5,
      },
      { label: "B", type: "power", x0: 50, y0: 15, heading: 180, speed: 12 },
    ],
    actions: [
      {
        id: "sail_keeps_clear",
        label: "Sailing vessel A keeps clear of the channel",
        correct: true,
      },
      {
        id: "power_gives_way",
        label: "Power vessel B gives way (Rule 18)",
        correct: false,
      },
      {
        id: "both_alter_stbd",
        label: "Both alter to starboard (Rule 14)",
        correct: false,
      },
      {
        id: "hold",
        label: "Hold course — sailing vessels are always stand-on",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  power_overtaking_sail: {
    id: "power_overtaking_sail",
    title: "Power overtaking sailing",
    setup:
      "You are vessel A, a motor yacht under power, approaching a sailing vessel from astern on the same heading. Your relative bearing places you well abaft B's beam.",
    rule: "Rule 13 — Overtaking.",
    explanation:
      "Rule 13(a) overrides everything in Section II: any vessel overtaking shall keep out of the way of the vessel being overtaken. A vessel is overtaking if approaching from more than 22.5° abaft the beam.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 90, heading: 0, speed: 14 },
      {
        label: "B",
        type: "sailing-stbd-tack",
        x0: 50,
        y0: 55,
        heading: 0,
        speed: 5,
      },
    ],
    actions: [
      {
        id: "a_keeps_clear",
        label: "Power vessel A (overtaking) keeps clear",
        correct: true,
      },
      {
        id: "b_gives_way_rule_18",
        label: "Sailing vessel B gives way (Rule 18 applies)",
        correct: false,
      },
      { id: "both_to_stbd", label: "Both alter to starboard", correct: false },
      { id: "a_holds_course", label: "A holds course; B alters", correct: false },
    ],
    durationMs: 4000,
  },
  sail_overtaking_power: {
    id: "sail_overtaking_power",
    title: "Sail overtaking power",
    setup:
      "You are vessel A, a sailing yacht making 12 knots in fresh breeze. You are approaching a motor cruiser (vessel B) from astern.",
    rule: "Rule 13 — Overtaking.",
    explanation:
      "Rule 13 trumps Rule 18. Even though a power-driven vessel would normally give way to a sailing vessel, the overtaking vessel always keeps clear. This is the most-tested 'gotcha' in the Rule 13 syllabus.",
    vessels: [
      {
        label: "A",
        type: "sailing-stbd-tack",
        x0: 50,
        y0: 90,
        heading: 0,
        speed: 12,
      },
      { label: "B", type: "power", x0: 50, y0: 65, heading: 0, speed: 5 },
    ],
    actions: [
      {
        id: "a_keeps_clear",
        label: "Sailing vessel A (overtaking) keeps clear",
        correct: true,
      },
      {
        id: "b_gives_way",
        label: "Power vessel B gives way (Rule 18 — power gives way to sail)",
        correct: false,
      },
      { id: "both_to_stbd", label: "Both alter to starboard", correct: false },
      { id: "a_holds_course", label: "A holds course; B alters", correct: false },
    ],
    durationMs: 4000,
  },
  power_meets_ram: {
    id: "power_meets_ram",
    title: "Power meets restricted-ability vessel",
    setup:
      "Vessel A is power-driven, under way. Vessel B is engaged in cable-laying — restricted in her ability to manoeuvre, showing the red-white-red all-round vertical arrangement.",
    rule: "Rule 18(a)(ii).",
    explanation:
      "A power-driven vessel under way shall keep out of the way of a vessel restricted in her ability to manoeuvre. RAM sits high in the Rule 18 hierarchy — sailing vessels and fishing vessels also give way to a RAM (Rules 18(b)(ii) and 18(c)(i)).",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 55, heading: 60, speed: 10 },
      { label: "B", type: "ram", x0: 60, y0: 30, heading: 200, speed: 3 },
    ],
    actions: [
      { id: "a_gives_way", label: "Power vessel A gives way", correct: true },
      {
        id: "b_alters",
        label: "RAM vessel B alters course",
        correct: false,
      },
      {
        id: "stbd_rule",
        label: "Apply crossing rule — vessel on stbd is stand-on",
        correct: false,
      },
      { id: "both_stbd", label: "Both alter to starboard", correct: false },
    ],
    durationMs: 4000,
  },
  sail_meets_fishing: {
    id: "sail_meets_fishing",
    title: "Sail meets fishing",
    setup:
      "Vessel A is a sailing yacht under sail alone. Vessel B is a trawler engaged in fishing.",
    rule: "Rule 18(b)(iii).",
    explanation:
      "A sailing vessel under way shall keep out of the way of a vessel engaged in fishing. The Rule 18 hierarchy is unidirectional — each category gives way to all categories above it (NUC, RAM, constrained-by-draft, fishing).",
    vessels: [
      {
        label: "A",
        type: "sailing-stbd-tack",
        x0: 15,
        y0: 55,
        heading: 90,
        speed: 7,
      },
      { label: "B", type: "fishing", x0: 70, y0: 35, heading: 180, speed: 4 },
    ],
    actions: [
      { id: "a_gives_way", label: "Sailing vessel A gives way", correct: true },
      {
        id: "b_gives_way",
        label: "Fishing vessel B gives way (more manoeuvrable)",
        correct: false,
      },
      {
        id: "stbd_rule",
        label: "Apply Rule 12 (different tacks)",
        correct: false,
      },
      { id: "both_to_stbd", label: "Both alter to starboard", correct: false },
    ],
    durationMs: 4000,
  },
  doubt_signal: {
    id: "doubt_signal",
    title: "Doubt as to other vessel's intentions",
    setup:
      "You see vessel B approaching in good visibility on a steady bearing. You cannot understand her intentions — she does not appear to be giving way, but you are uncertain whether she has seen you. What sound signal do you make?",
    rule: "Rule 34(d) — Manoeuvring and warning signals.",
    explanation:
      "Rule 34(d) — when vessels in sight of each other are approaching and either vessel fails to understand the intentions or actions of the other, or is in doubt whether sufficient action is being taken, the vessel in doubt shall immediately indicate it by giving at least five short and rapid blasts on the whistle. May be supplemented by 5+ short flashes of an all-round white light.",
    vessels: [
      { label: "A", type: "power", x0: 12, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 70, y0: 20, heading: 200, speed: 10 },
    ],
    actions: [
      {
        id: "five_short_rapid",
        label: "5 or more short and rapid blasts on the whistle",
        correct: true,
      },
      {
        id: "one_short",
        label: "1 short blast (~1 s) — 'I am altering to starboard'",
        correct: false,
      },
      {
        id: "two_short",
        label: "2 short blasts — 'I am altering to port'",
        correct: false,
      },
      {
        id: "prolonged",
        label: "1 prolonged blast (~5 s) — 'I'm under way'",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  manoeuvre_signal_stbd: {
    id: "manoeuvre_signal_stbd",
    title: "Manoeuvring signal — altering to starboard",
    setup:
      "Vessels in sight of one another. You are about to alter course to starboard to give way to a crossing vessel. What sound signal accompanies the manoeuvre?",
    rule: "Rule 34(a) — Manoeuvring and warning signals.",
    explanation:
      "Rule 34(a)(i) — one short blast means 'I am altering my course to starboard'. Two short = 'altering to port'. Three short = 'I am operating astern propulsion'. Required when in sight and taking action authorised or required by these Rules.",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 70, y0: 90, heading: 340, speed: 10 },
    ],
    actions: [
      {
        id: "one_short",
        label: "1 short blast (~1 second)",
        correct: true,
      },
      { id: "two_short", label: "2 short blasts", correct: false },
      { id: "three_short", label: "3 short blasts", correct: false },
      {
        id: "one_prolonged",
        label: "1 prolonged blast (~5 seconds)",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  tss_crossing: {
    id: "tss_crossing",
    title: "Crossing a Traffic Separation Scheme",
    setup:
      "You need to cross a Traffic Separation Scheme (TSS). The general direction of traffic flow is east-west on both lanes.",
    rule: "Rule 10 — Traffic separation schemes.",
    explanation:
      "Rule 10(c): a vessel shall, so far as practicable, avoid crossing traffic lanes, but if obliged to do so shall cross on a heading as nearly as practicable at right angles to the general direction of traffic flow. The crossing should be made decisively and clearly visible to other traffic.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 90, heading: 0, speed: 10 },
      { label: "B", type: "power", x0: 8, y0: 50, heading: 90, speed: 12 },
    ],
    actions: [
      {
        id: "right_angles",
        label: "Cross at right angles to traffic flow",
        correct: true,
      },
      {
        id: "diagonal",
        label: "Cross diagonally to minimise time in the scheme",
        correct: false,
      },
      {
        id: "follow_flow",
        label: "Join the flow temporarily then exit on the other side",
        correct: false,
      },
      {
        id: "do_not_cross",
        label: "Don't cross — go around the scheme",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  give_way_didnt_alter: {
    id: "give_way_didnt_alter",
    title: "Crossing — you gave way but B is not clear",
    setup:
      "You earlier altered A to starboard to pass astern of B (correct under Rule 16). Three minutes on, B has matched your alteration and your bearing to her is still steady. CPA is shrinking.",
    rule: "Rule 8 + Rule 17(b).",
    explanation:
      "Rule 8(d): action taken to avoid collision shall result in passing at a safe distance, and the effectiveness of the action shall be carefully checked until the other vessel is finally past and clear. A second substantial alteration — usually further to starboard, or a deceleration — is required. Avoid altering to port (Rule 17(c) prohibits it on this geometry).",
    vessels: [
      { label: "A", type: "power", x0: 25, y0: 65, heading: 110, speed: 10 },
      { label: "B", type: "power", x0: 75, y0: 35, heading: 200, speed: 10 },
    ],
    actions: [
      {
        id: "further_stbd",
        label: "Further substantial alteration to starboard + reduce speed",
        correct: true,
      },
      {
        id: "alter_port",
        label: "Alter to port to pass ahead of B",
        correct: false,
      },
      {
        id: "hold_course",
        label: "Hold course — you've already given way",
        correct: false,
      },
      {
        id: "stop",
        label: "Stop engines completely",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  head_on_b_did_not_alter: {
    id: "head_on_b_did_not_alter",
    title: "Head-on — you altered, the other vessel didn't",
    setup:
      "Two power vessels were on reciprocal courses. You altered substantially to starboard (correct under Rule 14). Vessel B has held her course and is now fine on your port bow — but still closing.",
    rule: "Rule 14 + Rule 8 — sustained avoiding action.",
    explanation:
      "Rule 14 expected both vessels to alter to starboard. Since B didn't, a further alteration to starboard (and/or speed reduction) is needed to ensure passing at a safe distance. Sound 5 short rapid blasts (Rule 34(d)) to indicate doubt. Never alter to port across her bow.",
    vessels: [
      { label: "A", type: "power", x0: 60, y0: 85, heading: 30, speed: 10 },
      { label: "B", type: "power", x0: 50, y0: 15, heading: 180, speed: 10 },
    ],
    actions: [
      {
        id: "further_stbd_and_signal",
        label: "Further alteration to starboard + 5 short blasts",
        correct: true,
      },
      {
        id: "port_to_clear",
        label: "Alter back to port to pass to her starboard",
        correct: false,
      },
      {
        id: "increase_speed",
        label: "Increase speed and pass close ahead",
        correct: false,
      },
      {
        id: "hold",
        label: "Hold course — you've done enough",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  constrained_by_draft_meets_yacht: {
    id: "constrained_by_draft_meets_yacht",
    title: "Yacht meets constrained-by-draft vessel in a channel",
    setup:
      "You are vessel A — a sailing yacht (4 m draft) — transiting a buoyed deep-water channel. Vessel B is a tanker showing three all-round red lights — constrained by her draft — proceeding up the channel toward you.",
    rule: "Rule 18(d) + Rule 9.",
    explanation:
      "Rule 18(d)(i): any vessel other than a vessel not under command or a vessel restricted in her ability to manoeuvre shall, if the circumstances of the case admit, avoid impeding the safe passage of a vessel constrained by her draft. Combined with Rule 9(b), the sailing yacht must keep clear and not impede — leave the channel if necessary.",
    vessels: [
      {
        label: "A",
        type: "sailing-stbd-tack",
        x0: 50,
        y0: 80,
        heading: 0,
        speed: 5,
      },
      {
        label: "B",
        type: "constrained",
        x0: 50,
        y0: 15,
        heading: 180,
        speed: 9,
      },
    ],
    actions: [
      {
        id: "yacht_keeps_clear",
        label: "Sailing yacht A leaves the channel / keeps well clear",
        correct: true,
      },
      {
        id: "b_must_alter",
        label: "Constrained vessel B must alter (Rule 18 — power gives way to sail)",
        correct: false,
      },
      {
        id: "both_alter_stbd",
        label: "Both alter to starboard as for head-on",
        correct: false,
      },
      {
        id: "yacht_holds",
        label: "Yacht holds her course — she is stand-on under Rule 18",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  constrained_by_draft_meets_power: {
    id: "constrained_by_draft_meets_power",
    title: "Power vessel meets constrained-by-draft vessel",
    setup:
      "You are vessel A — a power-driven motor yacht — crossing a deep-water channel. Vessel B is a tanker constrained by her draft, proceeding along the channel on your starboard bow.",
    rule: "Rule 18(d) — Constrained by her draft.",
    explanation:
      "Rule 18(d)(i): any vessel other than a NUC or RAM shall, if the circumstances admit, avoid impeding the safe passage of a vessel constrained by her draft. Note the phrasing — 'avoid impeding' is not quite 'keep out of the way' (which is the language of Rule 16). In practice you treat her as a higher-priority vessel and stay clear.",
    vessels: [
      { label: "A", type: "power", x0: 12, y0: 55, heading: 90, speed: 10 },
      {
        label: "B",
        type: "constrained",
        x0: 80,
        y0: 80,
        heading: 320,
        speed: 9,
      },
    ],
    actions: [
      {
        id: "a_avoids_impeding",
        label: "A alters early to avoid impeding B's safe passage",
        correct: true,
      },
      {
        id: "stbd_stand_on",
        label: "B is on A's stbd — apply Rule 15 crossing, A gives way",
        correct: false,
      },
      {
        id: "b_alters",
        label: "Constrained vessel B alters course around A",
        correct: false,
      },
      {
        id: "both_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  overtaking_didnt_clear: {
    id: "overtaking_didnt_clear",
    title: "Overtaking — vessel ahead has slowed unexpectedly",
    setup:
      "You are vessel A, overtaking B to starboard. B has slowed and altered slightly to port, increasing your closing rate. You are now well past abeam but not finally past and clear.",
    rule: "Rule 13 + Rule 8(d).",
    explanation:
      "Rule 13(d): any subsequent alteration of bearing does not relieve the overtaking vessel of her duty to keep clear of the other vessel until finally past and clear. Rule 8(d) requires action that results in passing at a safe distance, checked until past and clear. Pass farther to starboard or slow until properly clear.",
    vessels: [
      { label: "A", type: "power", x0: 55, y0: 50, heading: 0, speed: 12 },
      { label: "B", type: "power", x0: 45, y0: 60, heading: 350, speed: 5 },
    ],
    actions: [
      {
        id: "further_stbd",
        label: "Further alteration to starboard to open distance",
        correct: true,
      },
      {
        id: "drop_back",
        label: "Slow down and drop back astern of B",
        correct: false,
      },
      {
        id: "alter_port_back",
        label: "Alter back to port across her stern",
        correct: false,
      },
      {
        id: "hold",
        label: "Hold course — B's alteration is her problem",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  risk_of_collision_bearing: {
    id: "risk_of_collision_bearing",
    title: "Is there a risk of collision?",
    setup:
      "Vessel B is approaching on your starboard bow. Over several minutes her compass bearing has not appreciably changed while the range steadily decreases. Both power-driven, open water, good visibility.",
    rule: "Rule 7 — Risk of collision.",
    explanation:
      "Rule 7(d)(i): risk of collision shall be deemed to exist if the compass bearing of an approaching vessel does not appreciably change. A steady bearing with decreasing range means risk exists — you must act under the steering rules now (B is on your starboard, so this is a Rule 15 crossing in which you give way). Don't wait for certainty.",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 70, y0: 90, heading: 340, speed: 10 },
    ],
    actions: [
      {
        id: "act_now",
        label: "Treat risk of collision as existing and take early action",
        correct: true,
      },
      {
        id: "wait_closer",
        label: "No risk yet — wait until the range is much closer to decide",
        correct: false,
      },
      {
        id: "needs_bearing_change",
        label: "Risk only exists if the bearing is changing",
        correct: false,
      },
      {
        id: "cant_judge",
        label: "Maintain speed; risk of collision can't be judged by bearing",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  safe_speed_fog: {
    id: "safe_speed_fog",
    title: "Safe speed in fog",
    setup:
      "You are power-driven in dense fog with radar operational and traffic reported in the area. What governs the speed you should be making?",
    rule: "Rule 6 — Safe speed.",
    explanation:
      "Rule 6 requires a safe speed so you can take proper and effective action and stop within a distance appropriate to the prevailing circumstances. In restricted visibility the factors include visibility, traffic density, stopping distance and the characteristics and limitations of your radar. Reduce to a speed at which you can stop within the distance you have verified is clear.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 85, heading: 0, speed: 8 },
      { label: "B", type: "power", x0: 52, y0: 25, heading: 180, speed: 6 },
    ],
    actions: [
      {
        id: "stop_within_clear",
        label: "Reduce to a speed allowing you to stop within the assured-clear distance",
        correct: true,
      },
      {
        id: "radar_full_warning",
        label: "Maintain cruising speed since radar gives full warning",
        correct: false,
      },
      {
        id: "match_traffic",
        label: "Match the speed of surrounding traffic",
        correct: false,
      },
      {
        id: "engine_only",
        label: "Safe speed depends only on engine power, not visibility",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  narrow_channel_keep_starboard: {
    id: "narrow_channel_keep_starboard",
    title: "Narrow channel — which side do you keep?",
    setup:
      "You are vessel A, power-driven, proceeding along a narrow buoyed channel. Vessel B is coming the other way. Where should you keep within the channel?",
    rule: "Rule 9(a) — Narrow channels.",
    explanation:
      "Rule 9(a): a vessel proceeding along the course of a narrow channel or fairway shall keep as near to the outer limit which lies on her starboard side as is safe and practicable. Both vessels keeping starboard means you pass port-to-port, cutting head-on risk.",
    vessels: [
      { label: "A", type: "power", x0: 58, y0: 80, heading: 0, speed: 8 },
      { label: "B", type: "power", x0: 42, y0: 20, heading: 180, speed: 8 },
    ],
    actions: [
      {
        id: "keep_starboard",
        label: "Keep to the starboard outer limit of the channel",
        correct: true,
      },
      {
        id: "keep_port",
        label: "Keep to the port side of the channel",
        correct: false,
      },
      {
        id: "keep_centre",
        label: "Stay in the centre of the channel for maximum depth",
        correct: false,
      },
      {
        id: "either_side",
        label: "Either side is acceptable for a small vessel",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  narrow_channel_crossing: {
    id: "narrow_channel_crossing",
    title: "Crossing a narrow channel",
    setup:
      "You are vessel A, about to cross a narrow channel. Vessel B, a coaster, is proceeding along the channel and can navigate safely only within it.",
    rule: "Rule 9(d) — Narrow channels.",
    explanation:
      "Rule 9(d): a vessel shall not cross a narrow channel or fairway if such crossing impedes the passage of a vessel which can safely navigate only within such channel. You hold off and time your crossing so as not to impede B — the crossing rule does not give you priority here.",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 55, heading: 90, speed: 9 },
      { label: "B", type: "power", x0: 50, y0: 15, heading: 180, speed: 11 },
    ],
    actions: [
      {
        id: "hold_off",
        label: "Hold off and cross only when it won't impede B",
        correct: true,
      },
      {
        id: "cross_ahead",
        label: "Cross ahead — B must give way to a vessel on her starboard side",
        correct: false,
      },
      {
        id: "both_stbd",
        label: "Both alter to starboard as in a crossing situation",
        correct: false,
      },
      {
        id: "b_stops",
        label: "B must stop to let you cross",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  sail_diff_tacks_reversed: {
    id: "sail_diff_tacks_reversed",
    title: "Sailing — different tacks (you stand on)",
    setup:
      "Two sailing vessels approaching. Vessel A has the wind on her starboard side; vessel B has the wind on her port side.",
    rule: "Rule 12 — Sailing vessels.",
    explanation:
      "Rule 12(a)(i): when each has the wind on a different side, the vessel which has the wind on the port side keeps out of the way of the other. B is on the port tack, so B gives way; A (starboard tack) stands on.",
    vessels: [
      {
        label: "A",
        type: "sailing-stbd-tack",
        x0: 12,
        y0: 50,
        heading: 90,
        speed: 8,
      },
      {
        label: "B",
        type: "sailing-port-tack",
        x0: 88,
        y0: 50,
        heading: 270,
        speed: 8,
      },
    ],
    actions: [
      {
        id: "b_gives_way",
        label: "Vessel B (port tack) gives way",
        correct: true,
      },
      {
        id: "a_gives_way",
        label: "Vessel A (starboard tack) gives way",
        correct: false,
      },
      {
        id: "windward_gives_way",
        label: "The windward vessel gives way",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  head_on_recognise: {
    id: "head_on_recognise",
    title: "Head-on at night — both sidelights in view",
    setup:
      "At night you see ahead another power-driven vessel showing two masthead lights in a line and both her red and green sidelights. The range is closing.",
    rule: "Rule 14 — Head-on situation.",
    explanation:
      "Seeing both sidelights and the masthead lights in line means you are meeting nearly head-on (Rule 14(b)). Rule 14(a): each vessel alters course to starboard so as to pass on the port side of the other. Rule 14(c): if in doubt that a head-on situation exists, assume it does and act accordingly.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 85, heading: 0, speed: 10 },
      { label: "B", type: "power", x0: 50, y0: 15, heading: 180, speed: 10 },
    ],
    actions: [
      {
        id: "both_to_stbd",
        label: "Both vessels alter to starboard, pass port-to-port",
        correct: true,
      },
      {
        id: "both_to_port",
        label: "Both vessels alter to port",
        correct: false,
      },
      {
        id: "stbd_side_stands_on",
        label: "The vessel with the other on her port side stands on",
        correct: false,
      },
      {
        id: "hold_and_signal",
        label: "Hold course and sound one prolonged blast",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  fishing_meets_ram: {
    id: "fishing_meets_ram",
    title: "Fishing meets restricted-ability vessel",
    setup:
      "Vessel A is a trawler engaged in fishing. Vessel B is a dredger restricted in her ability to manoeuvre, showing the red-white-red all-round vertical arrangement.",
    rule: "Rule 18(c)(ii) — Responsibilities between vessels.",
    explanation:
      "Rule 18(c): a vessel engaged in fishing when under way shall, so far as possible, keep out of the way of a vessel not under command and a vessel restricted in her ability to manoeuvre. Fishing sits below RAM in the hierarchy — so the trawler gives way to the dredger.",
    vessels: [
      { label: "A", type: "fishing", x0: 12, y0: 55, heading: 80, speed: 5 },
      { label: "B", type: "ram", x0: 70, y0: 30, heading: 210, speed: 3 },
    ],
    actions: [
      {
        id: "a_gives_way",
        label: "Fishing vessel A gives way",
        correct: true,
      },
      {
        id: "b_gives_way",
        label: "RAM vessel B gives way (she should keep clear of working craft)",
        correct: false,
      },
      {
        id: "stbd_rule",
        label: "Apply the crossing rule — vessel on the starboard side stands on",
        correct: false,
      },
      {
        id: "both_to_stbd",
        label: "Both alter to starboard",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  overtaking_in_doubt: {
    id: "overtaking_in_doubt",
    title: "Overtaking or crossing? In doubt",
    setup:
      "You are approaching vessel B from a position close to her beam. You are unsure whether you are more than 22.5° abaft her beam (overtaking) or forward of it (crossing).",
    rule: "Rule 13(c) — Overtaking.",
    explanation:
      "Rule 13(c): when a vessel is in any doubt as to whether she is overtaking another, she shall assume that this is the case and act accordingly — keeping out of the way of the other until finally past and clear. The overtaking duty is the safer assumption.",
    vessels: [
      { label: "A", type: "power", x0: 72, y0: 78, heading: 315, speed: 12 },
      { label: "B", type: "power", x0: 50, y0: 55, heading: 0, speed: 6 },
    ],
    actions: [
      {
        id: "assume_overtaking",
        label: "Assume you are overtaking and keep clear",
        correct: true,
      },
      {
        id: "assume_crossing",
        label: "Assume it's a crossing and apply Rule 15",
        correct: false,
      },
      {
        id: "hold_until_certain",
        label: "Hold course until the geometry is certain",
        correct: false,
      },
      {
        id: "signal_and_hold",
        label: "Sound one prolonged blast and maintain speed",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  in_extremis: {
    id: "in_extremis",
    title: "In extremis — collision imminent",
    setup:
      "You are the stand-on vessel in a crossing. The give-way vessel has failed to act and collision is now imminent — following the steering rules alone will not prevent it.",
    rule: "Rule 2 — Responsibility.",
    explanation:
      "Rule 2(b): in construing and complying with these Rules, due regard shall be had to any special circumstances, including the limitations of the vessels involved, which may make a departure from these Rules necessary to avoid immediate danger. In extremis you take whatever action best avoids collision — including action the steering rules would otherwise prohibit.",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 45, y0: 20, heading: 200, speed: 10 },
    ],
    actions: [
      {
        id: "best_action",
        label: "Depart from the rules as needed — take the action that best avoids collision",
        correct: true,
      },
      {
        id: "hold_as_standon",
        label: "Hold course and speed — the stand-on vessel never deviates",
        correct: false,
      },
      {
        id: "only_starboard",
        label: "You may only ever alter to starboard, whatever the geometry",
        correct: false,
      },
      {
        id: "signal_only",
        label: "Sound five short blasts but make no alteration",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  risk_large_vessel_close: {
    id: "risk_large_vessel_close",
    title: "Risk with a large vessel at close range",
    setup:
      "You are closing a very large vessel at close range. Her compass bearing is changing appreciably, yet the range is small and still closing.",
    rule: "Rule 7 — Risk of collision.",
    explanation:
      "Rule 7(d)(ii): risk may sometimes exist even when an appreciable bearing change is evident, particularly when approaching a very large vessel or a tow, or when approaching a vessel at close range. Don't be reassured by the bearing change — treat risk as present and act in good time.",
    vessels: [
      { label: "A", type: "power", x0: 15, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "constrained", x0: 70, y0: 40, heading: 200, speed: 9 },
    ],
    actions: [
      {
        id: "assume_risk",
        label: "Assume risk of collision may exist and take avoiding action early",
        correct: true,
      },
      {
        id: "no_risk_bearing",
        label: "No risk — the bearing is changing, so you will pass clear",
        correct: false,
      },
      {
        id: "wait_and_see",
        label: "Hold on; reassess only if the bearing steadies",
        correct: false,
      },
      {
        id: "speed_up",
        label: "Increase speed to open the range quickly",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  manoeuvre_signal_port: {
    id: "manoeuvre_signal_port",
    title: "Manoeuvring signal — altering to port",
    setup:
      "Vessels in sight of one another. You are altering course to port. What sound signal accompanies the manoeuvre?",
    rule: "Rule 34(a)(ii) — Manoeuvring and warning signals.",
    explanation:
      "Rule 34(a): two short blasts means 'I am altering my course to port'. One short = altering to starboard; three short = operating astern propulsion. Required when in sight and taking action authorised or required by these Rules.",
    vessels: [
      { label: "A", type: "power", x0: 10, y0: 50, heading: 90, speed: 10 },
      { label: "B", type: "power", x0: 70, y0: 20, heading: 200, speed: 8 },
    ],
    actions: [
      { id: "two_short", label: "2 short blasts", correct: true },
      { id: "one_short", label: "1 short blast", correct: false },
      { id: "three_short", label: "3 short blasts", correct: false },
      {
        id: "one_prolonged",
        label: "1 prolonged blast (~5 seconds)",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
  overtaking_signal_narrow_channel: {
    id: "overtaking_signal_narrow_channel",
    title: "Overtaking signal in a narrow channel",
    setup:
      "In a narrow channel you intend to overtake vessel B and pass on her starboard side. She must take action to permit safe passing. What signal do you sound?",
    rule: "Rule 34(c)(i) — Overtaking in a narrow channel.",
    explanation:
      "Rule 34(c)(i): a vessel intending to overtake in a narrow channel and pass on the other's starboard side sounds two prolonged blasts followed by one short blast. (To pass on the port side: two prolonged then two short.) The vessel about to be overtaken, if in agreement, answers one prolonged, one short, one prolonged, one short.",
    vessels: [
      { label: "A", type: "power", x0: 50, y0: 90, heading: 0, speed: 14 },
      { label: "B", type: "power", x0: 50, y0: 58, heading: 0, speed: 5 },
    ],
    actions: [
      {
        id: "two_prolonged_one_short",
        label: "Two prolonged blasts followed by one short blast",
        correct: true,
      },
      {
        id: "two_prolonged_two_short",
        label: "Two prolonged blasts followed by two short blasts",
        correct: false,
      },
      {
        id: "one_short",
        label: "One short blast",
        correct: false,
      },
      {
        id: "five_short",
        label: "Five short and rapid blasts",
        correct: false,
      },
    ],
    durationMs: 4000,
  },
});

export const SCENARIO_IDS: readonly string[] = Object.keys(SCENARIOS);
